import React from 'react';
import { Box, Heading, HStack, Icon, Text } from '@chakra-ui/react';
import { FaArrowRight } from 'react-icons/fa';
import OptimizedImage from '../common/OptimizedImage';

// Shared person card used by the team, board, and surgeons pages.
//
// Every headshot is served from /team/portraits, recropped to one 4:5 frame
// with the same head size and eye line, so a roster reads as one set of
// portraits instead of a mix of wide and tight shots. The card shows the
// person's role, name and a short excerpt; the full bio (still in the page,
// just clamped) opens in the profile panel via `onOpen`.
export const PersonPortrait = ({ member, rounded = '18px', ...props }) => (
  <Box
    position="relative"
    borderRadius={rounded}
    overflow="hidden"
    bg="mist.100"
    sx={{ aspectRatio: '4 / 5' }}
    // Keeps the rounded clip in place while the image inside scales.
    transform="translateZ(0)"
    {...props}
  >
    {member.image ? (
      <OptimizedImage
        src={member.image}
        alt={member.name ? `Portrait of ${member.name}` : ''}
        w="100%"
        h="100%"
        objectFit="cover"
        objectPosition="center top"
        display="block"
        transition="transform 1.1s cubic-bezier(0.16,1,0.3,1)"
        _groupHover={{ transform: 'scale(1.035)' }}
      />
    ) : (
      <Box position="absolute" inset={0} display="grid" placeItems="center" color="ink.400">
        <svg width="96" height="96" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="M6 21V19C6 16.7909 7.79086 15 10 15H14C16.2091 15 18 16.7909 18 19V21"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </Box>
    )}
    {/* A faint ink falloff at the foot of every portrait. The source photos
        were shot on different backdrops; a shared shadow at the base ties
        them together as one set. */}
    <Box
      position="absolute"
      inset={0}
      bg="linear-gradient(180deg, rgba(14,47,58,0) 62%, rgba(14,47,58,0.22) 100%)"
      pointerEvents="none"
    />
  </Box>
);

const PersonCard = ({ member, onOpen }) => {
  const label = member.name || member.title;

  return (
    <Box
      role="group"
      position="relative"
      h="100%"
      display="flex"
      flexDirection="column"
      bg="white"
      borderRadius="24px"
      border="1px solid"
      borderColor="rgba(14,47,58,0.07)"
      boxShadow="cardSoft"
      p={2.5}
      transition="transform 0.5s cubic-bezier(0.16,1,0.3,1), box-shadow 0.5s cubic-bezier(0.16,1,0.3,1)"
      _hover={{ transform: 'translateY(-4px)', boxShadow: 'card' }}
      _focusWithin={{ boxShadow: 'card' }}
    >
      <PersonPortrait member={member} />

      <Box px={{ base: 3, md: 3.5 }} pt={6} pb={3} display="flex" flexDirection="column" flex="1">
        {member.title && (
          <Text
            fontSize="11px"
            fontWeight="700"
            color="brand.700"
            letterSpacing="0.16em"
            textTransform="uppercase"
            lineHeight="1.5"
            mb={2}
          >
            {member.title}
          </Text>
        )}

        {member.name && (
          <Heading
            as="h3"
            fontSize={{ base: 'xl', md: '1.35rem' }}
            color="ink.900"
            fontWeight="600"
            letterSpacing="-0.02em"
            lineHeight="1.25"
          >
            {member.name}
          </Heading>
        )}

        {member.affiliations && (
          <Text mt={2} fontSize="sm" color="ink.500" lineHeight="1.55">
            {member.affiliations.join(' · ')}
          </Text>
        )}

        {member.bio && (
          // Clamped, not cut: the whole bio stays in the page for search
          // and screen readers, and the panel shows it in full.
          <Text mt={4} color="ink.600" fontSize="sm" lineHeight="1.7" noOfLines={3}>
            {member.bio}
          </Text>
        )}

        {onOpen && (
          <Box
            as="button"
            type="button"
            onClick={onOpen}
            aria-label={`Read the full bio of ${label}`}
            mt="auto"
            pt={5}
            alignSelf="flex-start"
            fontSize="sm"
            fontWeight="600"
            color="brand.800"
            // The button's hit area covers the whole card, so the portrait
            // and name open the profile too.
            _after={{ content: '""', position: 'absolute', inset: 0, borderRadius: '24px' }}
            _focusVisible={{ outline: 'none', _after: { boxShadow: '0 0 0 3px rgba(21,150,184,0.55)' } }}
          >
            <HStack spacing={2}>
              <span>Read full bio</span>
              <Icon
                as={FaArrowRight}
                boxSize={3}
                transition="transform 0.4s cubic-bezier(0.16,1,0.3,1)"
                _groupHover={{ transform: 'translateX(4px)' }}
              />
            </HStack>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PersonCard;
